import React from 'react';
import SectionHeading from './SectionHeading';
import Icon from './Icon';
import { LOCATION } from '../content/contacts';
import ukraineMap from '../assets/ukraine-map.svg';

/**
 * Позиції міст — у відсотках від ширини й висоти ілюстрації
 * (src/assets/ukraine-map.svg, viewBox 1000×650). Крапки й дуги намальовані
 * в самому SVG, тут лише підписи: SVG віддається як <img>, і шрифти сайту до
 * тексту всередині нього не дісталися б. `side` — з якого боку від крапки
 * стоїть підпис, щоб він не лягав на дугу.
 */
const LVIV = { x: 21.94, y: 31.12 };
const CITIES = [
  { name: 'Київ', x: 52.04, y: 26.75, side: 'right' },
  { name: 'Харків', x: 78.38, y: 30.66, side: 'right' },
  { name: 'Вінниця', x: 42.32, y: 39.89, side: 'right' },
  { name: 'Дніпро', x: 73.46, y: 48.03, side: 'right' },
  { name: 'Одеса', x: 52.78, y: 70.65, side: 'bottom' },
  { name: 'Ужгород', x: 12.88, y: 43.23, side: 'bottom' }
];

const LABEL_SHIFT = {
  right: 'translate(10px, -50%)',
  bottom: 'translate(-50%, 10px)'
};

/**
 * «Де ми» — останній блок головної перед футером: Україна, Львів і дуги до
 * великих міст. Сенс блоку в одному реченні — «команда у Львові, клієнти по
 * всій Україні», — і дуги кажуть саме це.
 *
 * Раніше тут був знімок карти Львова з OpenStreetMap, але без точної адреси
 * карта міста нічого не показувала, крім того, що Львів великий. Ілюстрацію
 * обирали з трьох варіантів (крапкова карта регіону, Європа з Україною,
 * Україна з дугами).
 *
 * Геометрія — офіційні межі від ДНВП «Картографія» (державне підприємство
 * України; набір OCHA COD-AB, чинний на 01.09.2025): контур країни з Кримом і
 * всіма тимчасово окупованими територіями й межі 27 одиниць першого рівня,
 * включно з АР Крим і Севастополем. Межа Криму з Херсонщиною — така сама біла
 * лінія, як між будь-якими областями. Ліцензія CC BY-IGO вимагає згадати
 * джерело — згадка стоїть у футері (Footer.js), не під картою.
 * Перемальовувати — лише з цих самих даних: у поширених «світових» наборах
 * Крим буває позначений інакше.
 *
 * Файл 26 КБ, тож збірка віддає його окремим /static/media/…svg з хешем. Якби
 * він схуд до 10 КБ, CRA вбудувала б його data:-адресою, а CSP сайту (img-src
 * 'self') таку картинку заблокує.
 */
const Location = () => (
  <section id="location" className="py-16 lg:py-24 bg-white">
    <div className="section-container">
      <div className="grid gap-10 lg:gap-14 lg:grid-cols-12 items-center">
        <div className="lg:col-span-5">
          <SectionHeading title="Де ми" caption={`${LOCATION.city} · по всій Україні`} />
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-md">
            Наша команда — у Львові. Клієнтів супроводжуємо по всій Україні: реєстрація, ліцензування, ЕСОЗ і торговельна марка оформлюються дистанційно, без ваших поїздок.
          </p>

          <ul className="mt-6 space-y-3 list-none">
            <li className="flex items-center gap-3 text-slate-700">
              <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center">
                <Icon name="fa-location-dot" />
              </span>
              {LOCATION.street ? `${LOCATION.street}, ${LOCATION.city}` : `${LOCATION.city}, Україна`}
            </li>
            <li className="flex items-center gap-3 text-slate-700">
              <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center">
                <Icon name="fa-handshake" />
              </span>
              Онлайн — для клієнтів з будь-якого міста
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <div className="relative w-full aspect-[1000/650]">
            <img
              src={ukraineMap}
              alt="Карта України: Львів і дуги звідти до Києва, Харкова, Вінниці, Дніпра, Одеси й Ужгорода"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full"
            />

            {/* Підписи дублюють те, що вже сказано в alt, тож для диктора
                їх немає. */}
            <div aria-hidden="true">
              <span className="absolute w-0 h-0" style={{ left: `${LVIV.x}%`, top: `${LVIV.y}%` }}>
                <span className="absolute -left-2 -top-2 w-4 h-4 sm:-left-2.5 sm:-top-2.5 sm:w-5 sm:h-5 rounded-full bg-teal-700 ring-[3px] sm:ring-4 ring-white shadow-[0_4px_12px_rgba(15,23,42,0.25)]"></span>
                <span className="absolute right-3.5 sm:right-[18px] top-0 -translate-y-1/2 whitespace-nowrap rounded-full bg-white px-3 sm:px-3.5 py-0.5 sm:py-1 font-display font-bold text-[13px] sm:text-sm text-slate-900 shadow-[0_4px_10px_rgba(15,23,42,0.12),0_0_0_1px_rgba(15,23,42,0.04)]">
                  {LOCATION.city}
                </span>
              </span>

              {CITIES.map((city) => (
                <span
                  key={city.name}
                  className="absolute whitespace-nowrap text-[11px] sm:text-[12.5px] leading-none font-medium text-slate-600 [text-shadow:0_0_3px_#fff,0_0_3px_#fff,0_0_2px_#fff]"
                  style={{ left: `${city.x}%`, top: `${city.y}%`, transform: LABEL_SHIFT[city.side] }}
                >
                  {city.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default Location;
